'use client';

import Link from 'next/link';
import { usePostHog } from 'posthog-js/react';
import { useAuth } from './AuthProvider';
import { useSubscription } from './SubscriptionProvider';
import { planLabel } from '../../lib/pricing';

interface UsageIndicatorProps {
  variant: 'nav' | 'inline' | 'menu';
}

export function UsageIndicator({ variant }: UsageIndicatorProps) {
  const { user } = useAuth();
  const { tier, limits, usage, generationsRemaining, loading } = useSubscription();
  const posthog = usePostHog();

  if (loading) return null;

  // Signed-out visitors must sign in before generating cards.
  if (!user) return null;

  // Public plan labels do not expose the internal paid-generation allowance.
  if (tier !== 'free') {
    const badge = (
      <span className="inline-flex shrink-0 whitespace-nowrap px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-bold">
        {planLabel(tier)}
      </span>
    );
    return variant === 'inline' ? (
      <div className="flex items-center justify-center mb-4">{badge}</div>
    ) : badge;
  }

  const used = usage.generationsUsed;
  const limit = limits.generationsPerMonth;
  const remaining = generationsRemaining;

  // Color coding
  let colorClass: string;
  let warningText: string | null = null;
  if (remaining === 0) {
    colorClass = 'bg-red-50 text-red-600 border-red-200';
  } else if (remaining === 1) {
    colorClass = 'bg-amber-50 text-amber-600 border-amber-200';
    warningText = 'Last free lecture deck!';
  } else {
    colorClass = 'bg-primary/10 text-primary border-primary/20';
  }

  if (variant === 'nav') {
    return (
      <Link
        href="/#pricing"
        onClick={() => posthog?.capture('usage_indicator_clicked', { tier, generations_remaining: remaining })}
        className={`px-2 py-0.5 rounded-full text-xs font-bold border ${colorClass} hover:opacity-80 transition-opacity`}
      >
        {used}/{limit}
      </Link>
    );
  }

  // Inline variant with progress bar
  const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  let barColor: string;
  if (remaining === 0) barColor = 'bg-red-500';
  else if (remaining === 1) barColor = 'bg-amber-500';
  else barColor = 'bg-primary';

  return (
    <div className="w-full max-w-md mx-auto mb-4">
      <div className="flex items-center justify-between text-xs text-muted mb-1.5">
        <span>{used}/{limit} lecture decks used this month</span>
        {remaining === 0 ? (
          <Link href="/#pricing" className="text-primary font-medium hover:underline">
            Upgrade
          </Link>
        ) : warningText ? (
          <Link href="/#pricing" className="text-amber-600 font-medium hover:underline">
            {warningText} Upgrade →
          </Link>
        ) : null}
      </div>
      <div className="h-1.5 bg-muted-bg rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${barColor}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
